/**
 * StaffSessionsView
 * ─────────────────────────────────────────────────────────────────────────────
 * Vendor-facing list of every active and past session that has touched the
 * vendor's tenant. Primary use case: the owner spots an unknown device or a
 * staff member who left → revokes their session in one click.
 *
 * Columns: Status | User | Role | Device / IP | Started | Last activity | Actions
 *
 * Filters:
 *   - Status (ACTIVE / CLOSED)
 *   - Date range (from / to) on login timestamp
 *
 * Actions:
 *   - "Revoke" on an ACTIVE session → confirmation → DELETE /sessions/:id
 *
 * Feature flag: parent route registration is gated by REACT_APP_FEATURE_AUDIT_TRAIL.
 */

import React, { useCallback, useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
  Spinner,
  Alert,
  Modal,
  Table,
} from "react-bootstrap";
import moment from "moment";
import { apiGetVendorSessions, apiTerminateSession } from "../api/index";
import HelpTooltip from "../components/HelpTooltip";
import AppSelect from "../components/ui/AppSelect";

const REASON_LABELS = {
  EXPLICIT_LOGOUT: { label: "Logged out", colour: "#27ae60" },
  ADMIN_REVOKED: { label: "Admin revoked", colour: "#c0392b" },
  TENANT_REVOKED: { label: "Revoked by owner", colour: "#e67e22" },
  SESSION_EXPIRED: { label: "Expired", colour: "#7f8c8d" },
  TOKEN_REVOKED: { label: "Token revoked", colour: "#c0392b" },
};

function parseUA(ua = "") {
  const lower = ua.toLowerCase();
  if (lower.includes("iphone") || lower.includes("ios")) return { icon: "fa-mobile-alt", label: "iPhone" };
  if (lower.includes("ipad")) return { icon: "fa-tablet-alt", label: "iPad" };
  if (lower.includes("android")) return { icon: "fa-mobile-alt", label: "Android" };
  if (lower.includes("macintosh") || lower.includes("mac os")) return { icon: "fa-laptop", label: "Mac" };
  if (lower.includes("windows")) return { icon: "fa-desktop", label: "Windows" };
  if (lower.includes("linux")) return { icon: "fa-desktop", label: "Linux" };
  return { icon: "fa-question-circle", label: "Unknown" };
}

function RevokeModal({ show, session, onConfirm, onHide, submitting }) {
  if (!session) return null;
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Revoke session?</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          This will immediately log out <strong>{session.userEmail || String(session.userId).slice(-8)}</strong>.
          They will need to sign in again to regain access.
        </p>
        <p className="text-muted small mb-0">
          The revocation is recorded in your activity log with an immutable timestamp.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={submitting}>Cancel</Button>
        <Button variant="danger" onClick={onConfirm} disabled={submitting}>
          {submitting ? <Spinner size="sm" animation="border" /> : "Revoke session"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default function StaffSessionsView() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ status: "", from: "", to: "" });
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revokeSubmitting, setRevokeSubmitting] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);

  const load = useCallback(
    async (p = 1) => {
      setLoading(true);
      setError(null);
      try {
        const params = { page: p, limit: 25 };
        if (filters.status) params.status = filters.status;
        if (filters.from) params.from = filters.from;
        if (filters.to) params.to = filters.to;
        const res = await apiGetVendorSessions(params);
        const payload = res?.data || {};
        const newRows = payload.data || [];
        setRows((prev) => (p === 1 ? newRows : [...prev, ...newRows]));
        setHasMore(!!payload.hasMore);
        setTotal(payload.total || 0);
        setPage(p);
      } catch (e) {
        setError(e?.response?.data?.message || e.message || "Failed to load sessions");
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  useEffect(() => {
    load(1);
  }, [load]);

  const confirmRevoke = async () => {
    if (!revokeTarget) return;
    setRevokeSubmitting(true);
    try {
      await apiTerminateSession(revokeTarget._id);
      setToastMsg("Session revoked.");
      setRevokeTarget(null);
      load(1);
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Failed to revoke");
    } finally {
      setRevokeSubmitting(false);
    }
  };

  return (
    <Container fluid className="py-3">
      {/* Hero */}
      <div
        style={{
          background:
            "linear-gradient(135deg, #1a2b4a 0%, #2a4373 55%, #17385a 100%)",
          color: "white",
          borderRadius: 12,
          padding: "24px 28px",
          marginBottom: 20,
          boxShadow: "0 6px 24px rgba(26,43,74,0.22)",
          display: "flex",
          alignItems: "center",
          gap: 18,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            width: 54,
            height: 54,
            borderRadius: 12,
            background: "rgba(255,255,255,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <i
            className="nc-icon nc-lock-circle-open"
            style={{ fontSize: 24, color: "white" }}
          />
        </div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <div
            style={{
              fontSize: 11,
              opacity: 0.8,
              letterSpacing: "1.2px",
              fontWeight: 600,
            }}
          >
            AUDIT &amp; COMPLIANCE
          </div>
          <h2
            style={{
              fontWeight: 800,
              margin: "2px 0 6px",
              fontSize: 26,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            Staff sessions
            <HelpTooltip text="Every device that has signed in to your shop. Use this to spot unknown logins, revoke a session remotely if a phone is lost, or confirm which staff member was logged in at a given time." />
          </h2>
          <div style={{ fontSize: 13, opacity: 0.9 }}>
            Every device that has touched your shop, with the power to revoke
            access in one click. Showing{" "}
            <strong>{rows.length}</strong> of <strong>{total}</strong>{" "}
            sessions.
          </div>
        </div>
      </div>

      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Body>
              {toastMsg && <Alert variant="success" onClose={() => setToastMsg(null)} dismissible>{toastMsg}</Alert>}

              <Row className="mb-3 align-items-end">
                <Col md={3}>
                  <Form.Label className="small mb-1">Status</Form.Label>
                  <AppSelect block
                    size="sm"
                    value={filters.status}
                    onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  >
                    <option value="">All</option>
                    <option value="ACTIVE">Active</option>
                    <option value="CLOSED">Closed</option>
                  </AppSelect>
                </Col>
                <Col md={3}>
                  <Form.Label className="small mb-1">Logged in after</Form.Label>
                  <Form.Control
                    size="sm" type="date"
                    value={filters.from}
                    onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                  />
                </Col>
                <Col md={3}>
                  <Form.Label className="small mb-1">Logged in before</Form.Label>
                  <Form.Control
                    size="sm" type="date"
                    value={filters.to}
                    onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                  />
                </Col>
              </Row>

              {error && <Alert variant="danger">{error}</Alert>}

              {rows.length === 0 && !loading && !error && (
                <div
                  style={{
                    textAlign: "center",
                    padding: "32px 20px",
                    background: "#f8fafc",
                    border: "1px dashed #d8dee8",
                    borderRadius: 10,
                  }}
                >
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: "50%",
                      background: "#eaf0f9",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      marginBottom: 12,
                    }}
                  >
                    <i
                      className="nc-icon nc-lock-circle-open"
                      style={{ fontSize: 22, color: "#1a2b4a" }}
                    />
                  </div>
                  <div style={{ fontWeight: 700, color: "#1a2b4a" }}>
                    No sessions recorded yet
                  </div>
                  <div className="small text-muted mt-1">
                    As soon as you or any staff member signs in, the session
                    will appear here.
                  </div>
                </div>
              )}

              {rows.length > 0 && (
                <Table hover responsive size="sm" style={{ fontSize: 13 }}>
                  <thead style={{ background: "#f7f7f9" }}>
                    <tr>
                      <th>Status</th>
                      <th>User</th>
                      <th>Role</th>
                      <th>Device / IP</th>
                      <th>Started</th>
                      <th>Last activity</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((s) => {
                      const device = parseUA(s.userAgent || "");
                      const isActive = s.status === "ACTIVE";
                      const reasonMeta = s.terminationReason && REASON_LABELS[s.terminationReason];
                      return (
                        <tr key={s._id}>
                          <td>
                            {isActive ? (
                              <Badge bg="success">
                                <i className="fa fa-circle mr-1" style={{ fontSize: 8 }} /> Active
                              </Badge>
                            ) : (
                              <Badge style={{ background: reasonMeta?.colour || "#7f8c8d" }}>
                                {reasonMeta?.label || "Closed"}
                              </Badge>
                            )}
                          </td>
                          <td>
                            {s.userEmail || <span className="text-muted">—</span>}
                            <div style={{ fontFamily: "monospace", fontSize: 11, color: "#999" }}>
                              {String(s.userId).slice(-8)}
                            </div>
                          </td>
                          <td><span className="text-muted">{s.userRole || "—"}</span></td>
                          <td>
                            <i className={`fa ${device.icon} mr-1`} style={{ color: "#888" }} />
                            {device.label}
                            <div className="text-muted" style={{ fontSize: 11 }}>{s.ip || "—"}</div>
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {moment(s.loginAt).format("MMM D, h:mm A")}
                            <div className="text-muted" style={{ fontSize: 11 }}>
                              {moment(s.loginAt).fromNow()}
                            </div>
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {s.lastActivityAt
                              ? moment(s.lastActivityAt).fromNow()
                              : <span className="text-muted">—</span>}
                          </td>
                          <td className="text-end">
                            {isActive && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => setRevokeTarget(s)}
                              >
                                <i className="fa fa-sign-out-alt mr-1" /> Revoke
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}

              {loading && (
                <div className="text-center py-3">
                  <Spinner size="sm" animation="border" /> Loading…
                </div>
              )}

              {!loading && hasMore && (
                <div className="text-center mt-3">
                  <Button variant="outline-secondary" size="sm" onClick={() => load(page + 1)}>
                    Load more
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <RevokeModal
        show={!!revokeTarget}
        session={revokeTarget}
        submitting={revokeSubmitting}
        onHide={() => setRevokeTarget(null)}
        onConfirm={confirmRevoke}
      />
    </Container>
  );
}
